import * as formulajs from '@formulajs/formulajs';

import { formatDateAMPM, separateDateValues } from '../dates';

const DATE = (dateOrYear: string | Date | number, month?: number, day?: number) => {
  if (month || day) {
    return formulajs.DATE(dateOrYear, month, day);
  }

  const date = dateOrYear instanceof Date ? dateOrYear : new Date(dateOrYear);
  const [yearNow, monthNow, dayNow] = separateDateValues(date);
  return formulajs.DATE(yearNow, monthNow, dayNow);
};

const TIME = (timeOrHour: string | Date | number, minute?: number, second?: number) => {
  if (minute || second) {
    return new Date(0, 0, 0, Number(timeOrHour), minute, second);
  }

  if (typeof timeOrHour === 'string') {
    const [hourFrom, minuteFrom, secondFrom] = timeOrHour.split(':').map(Number);
    return new Date(0, 0, 0, hourFrom || 0, minuteFrom || 0, secondFrom || 0);
  }

  return timeOrHour;
};

const TIMEVALUE = TIME;

const DATEVALUE = (date: string) => {
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(date)) {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  const warns = [];

  warns.push('1/4 error parsing', date, 'as an english date');

  const intent = formulajs.DATEVALUE(date);

  const isIntentErrored = formulajs.ISERROR(intent);

  if (!isIntentErrored) {
    return intent;
  }

  warns.push('2/4 default intent to parse', date, 'as date failed');

  const isGermanFormat = date.includes('.');

  if (isGermanFormat) {
    try {
      const [day, month, year] = date.split('.').map(Number);
      return new Date(year, month - 1, day, 0, 0, 0, 0);
    } catch (e) {
      warns.push('3/4 error parsing', date, 'as german date:', e);
    }
  }

  try {
    const currentDate = new Date(date);
    currentDate.setHours(0, 0, 0, 0);
    return formulajs.DATEVALUE(currentDate.toLocaleDateString('en-US'));
  } catch (e) {
    warns.push('4/4 error parsing', date, 'as a date:', e);
  }

  if (warns.length > 0) {
    console.warn(warns);
  }
  console.error('Error parsing', date, 'will return', intent);
  return intent;
};

const DATEADD = (interval: string, amount: number, dateValue: Date): Date => {
  const date = new Date(dateValue.getTime());

  const int = interval.toLowerCase();

  switch (int) {
    case 'yyyy':
      date.setFullYear(date.getFullYear() + Math.trunc(amount));
      break;
    case 'q':
      date.setMonth(date.getMonth() + 3 * Math.trunc(amount));
      break;
    case 'm':
      date.setMonth(date.getMonth() + Math.trunc(amount));
      break;
    case 'y':
    case 'd':
      date.setDate(date.getDate() + Math.trunc(amount));
      break;
    case 'w':
    case 'ww':
      date.setDate(date.getDate() + 7 * Math.trunc(amount));
      break;
    case 'h':
      date.setTime(date.getTime() + amount * 60 * 60 * 1000);
      break;
    case 'n':
      date.setTime(date.getTime() + amount * 60 * 1000);
      break;
    case 's':
      date.setTime(date.getTime() + amount * 1000);
      break;
    default:
      throw new Error(`#VALUE!`);
  }

  return date;
};

type TimeCallback = (time: string) => number;

const handleTimeParts = (time: string, callback: TimeCallback) => {
  const now = new Date();
  const [hours, minutes, seconds] = time.split(':');

  now.setHours(time.includes('PM') ? parseInt(hours) + 12 : parseInt(hours));
  now.setMinutes(parseInt(minutes));
  now.setSeconds(parseInt(seconds));

  return callback(formatDateAMPM(now));
};

const HOUR = (time: string) => handleTimeParts(time, formulajs.HOUR);
const MINUTE = (time: string) => handleTimeParts(time, formulajs.MINUTE);
const SECOND = (time: string) => handleTimeParts(time, formulajs.SECOND);

const STDEV = (...args: number[]) => formulajs.STDEVA(...args);

const VAR = (...args: number[]) => formulajs.VARA(...args);

const FLOOR = (num: number, significance = 1) => formulajs.FLOOR(num, significance);

// Unit mapping from Infragistics DATEDIFF to FormulaJS DATEDIF
const INFRAGISTICS_TO_FORMULAJS_UNITS: Record<string, string> = {
  yyyy: 'Y', // years
  y: 'D', // days (day of year in Infragistics)
  d: 'D', // days
  w: 'D', // weeks (convert to days and divide by 7)
  ww: 'D', // weeks of year (convert to days and divide by 7)
  m: 'M', // months
  q: 'M', // quarters (convert to months and divide by 3)
  h: 'H', // hours (custom handling)
  n: 'N', // minutes (custom handling)
  s: 'S', // seconds (custom handling)
  ms: 'MS' // milliseconds (custom handling)
};

const DATEDIFF = (unit: string, date1: Date, date2: Date) => {
  const lowerUnit = unit.toLowerCase();
  const mappedUnit = INFRAGISTICS_TO_FORMULAJS_UNITS[lowerUnit];

  if (!mappedUnit) {
    throw new Error(`Unsupported unit: ${unit}`);
  }

  // Handle time-based units that aren't supported by DATEDIF
  const timeDiffMs = date2.getTime() - date1.getTime();

  switch (lowerUnit) {
    case 'h':
      return Math.floor(timeDiffMs / (1000 * 60 * 60));

    case 'n':
      return Math.floor(timeDiffMs / (1000 * 60));

    case 's':
      return Math.floor(timeDiffMs / 1000);

    case 'ms':
      return timeDiffMs;

    case 'w':
    case 'ww': {
      const daysDiff = Number(formulajs.DATEDIF(date1, date2, 'D'));
      return Math.floor(daysDiff / 7);
    }

    case 'q': {
      const monthsDiff = Number(formulajs.DATEDIF(date1, date2, 'M'));
      return Math.floor(monthsDiff / 3);
    }

    default:
      // Use FormulaJS DATEDIF for Y, M, D units
      return formulajs.DATEDIF(date1, date2, mappedUnit);
  }
};

const TRUE = () => true;

const FALSE = () => false;

const transformers = {
  DATE,
  DATEVALUE,
  TIME,
  TIMEVALUE,
  DATEADD,
  DATEDIFF,
  TRUE,
  FALSE,
  HOUR,
  MINUTE,
  SECOND,
  STDEV,
  VAR,
  FLOOR
};

export const populateFormulasForDebug = () => {
  const entries = Object.entries({ ...formulajs, ...transformers });

  if (window) {
    if ((window as any).formulasReady) {
      return;
    }

    entries.forEach(([name, fn]) => {
      (window as any)[name] = fn;
      (window as any).formulasReady = true;
    });
  } else {
    if ((globalThis as any).formulasReady) {
      return;
    }

    entries.forEach(([name, fn]) => {
      (globalThis as any)[name] = fn;
      (globalThis as any).formulasReady = true;
    });
  }
};

export default transformers;
